import { createRouter, createWebHistory } from "vue-router";
import HomeView from "../views/HomeView/index.vue";
import { useLoggedInUserStore } from "@/store/loggedInUser";
import { useProductStore } from "@/store/product";
import { useMarketSignalStore } from "@/store/marketSignal";
import { PRODUCTS } from "@/services/products";

const authGuard = async (to, from, next) => {
  const user = useLoggedInUserStore();
  await user.checkIfLoggedInAsAdmin();

  if (!user.isAuthenticated()) {
    if (!to.query.temp_token) {
      next({ name: "login", query: { redirect: to.path } });
      return;
    }

    // if temp token provided, try to validate
    const tempToken = to.query.temp_token;
    try {
      await user.validateTempToken(tempToken);
    } catch (invalidTokenError) {
      next({ name: "login", query: { redirect: to.path } });
      return;
    }
  }

  user.logActivity(to.fullPath);

  if (
    user.isRep &
    ((user.territory_id == null) & (user.region_id == null) ||
      user.assigned_empty_region)
  ) {
    // Check if user is setup
    next("/setup-required");
  } else if (!user.acceptedEula) {
    // check if they has not accepted the EULA
    next("/eula");
  } else {
    next();
  }
};

const leaderGuard = (to, from, next) => {
  const user = useLoggedInUserStore();
  if (user.isRep) {
    next("/");
  } else {
    next();
  }
};

const regionGuard = (to, from, next) => {
  const user = useLoggedInUserStore();
  const regionId = to.params.id;
  if (
    regionId &&
    !user.accessibleRegions.some((region) => region.id == regionId)
  ) {
    next({ name: "NotFound" });
  } else {
    next();
  }
};

const territoryGuard = (to, from, next) => {
  const user = useLoggedInUserStore();
  const territoryId = to.params.id;
  if (
    territoryId &&
    !user.accessibleTerritories.some((ter) => ter.id == territoryId)
  ) {
    next({ name: "NotFound" });
  } else {
    next();
  }
};

const adminGuard = (to, from, next) => {
  const user = useLoggedInUserStore();
  if (user.is_admin) {
    next();
  } else {
    next("/");
  }
};

const logout = (to, from, next) => {
  const user = useLoggedInUserStore();
  user.logout();
  next({ name: "login", query: { redirect: from.path } });
};

// Send "/" to the MarketSignal landing page when that product is selected.
const productHomeGuard = (to, from, next) => {
  const product = useProductStore();
  if (product.isMarketSignal) {
    next({ name: "marketSignalHome" });
  } else {
    next();
  }
};

// RepSignal routes send MarketSignal-only users to MarketSignal.
const repSignalGuard = (to, from, next) => {
  const product = useProductStore();
  if (!product.enableRepSignal && product.enableMarketSignal) {
    next({ name: "marketSignalHome" });
  } else {
    next();
  }
};

// MarketSignal routes require the company toggle; visiting one selects the product.
const marketSignalGuard = (to, from, next) => {
  const product = useProductStore();
  if (!product.enableMarketSignal) {
    next({ name: "home" });
    return;
  }
  if (product.selected !== PRODUCTS.MARKETSIGNAL) {
    product.select(PRODUCTS.MARKETSIGNAL).catch(() => {});
  }
  // Warm the dashboard list so landing and dashboard views render immediately.
  useMarketSignalStore().load();
  next();
};

const router = createRouter({
  history: createWebHistory(),
  scrollBehavior() {
    // always scroll to top
    return { top: 0 };
  },
  routes: [
    {
      path: "/rs/",
      name: "home",
      alias: "/",
      component: HomeView,
      beforeEnter: [authGuard, productHomeGuard],
      meta: {
        title: "Home",
      },
    },
    {
      path: "/login",
      name: "login",
      component: () => import("../views/LoginView.vue"),
      meta: {
        title: "Login",
      },
    },
    {
      path: "/forgot_password",
      name: "forgotPassword",
      component: () => import("../views/ForgotPasswordView.vue"),
      meta: {
        title: "Forgot Password",
      },
    },
    {
      path: "/logout",
      name: "logout",
      beforeEnter: logout,
    },
    {
      path: "/weekly_email_feedback",
      name: "emailFeedback",
      component: () => import("../views/EmailFeedbackView.vue"),
    },
    {
      path: "/explorer",
      name: "explorer",
      component: () => import("../views/ExplorerView/index.vue"),
      beforeEnter: [authGuard, repSignalGuard],
      meta: {
        title: "Explorer",
      },
    },
    {
      path: "/explorer/refresh",
      name: "explorerRefresh",
      component: () => import("../views/ExplorerView/Refresh.vue"),
      beforeEnter: [authGuard, adminGuard, repSignalGuard],
    },
    {
      path: "/market_summary",
      name: "marketSummary",
      component: () => import("../views/MarketSummaryView/index.vue"),
      beforeEnter: [authGuard, leaderGuard, repSignalGuard],
      meta: {
        title: "MarketSignal",
      },
    },
    {
      path: "/physician/:npi",
      name: "physician",
      component: () => import("../views/PhysicianView/index.vue"),
      beforeEnter: [authGuard, repSignalGuard],
    },
    {
      path: "/regions/:id?",
      name: "regions",
      component: () => import("../views/RegionsView/index.vue"),
      beforeEnter: [authGuard, regionGuard, repSignalGuard],
    },
    {
      path: "/regions/manager",
      name: "region_manager",
      component: () => import("../views/RegionsView/index.vue"),
      beforeEnter: [authGuard, leaderGuard, repSignalGuard],
      meta: {
        title: "Territories",
      },
    },
    {
      path: "/territory/:id",
      name: "territory",
      component: () => import("../views/TerritoryView.vue"),
      beforeEnter: [authGuard, territoryGuard, repSignalGuard],
    },
    {
      path: "/territory",
      name: "territory_search",
      component: () => import("../views/TerritoryView.vue"),
      beforeEnter: [authGuard, repSignalGuard],
    },
    {
      path: "/hospital/:ccn/:tab?",
      name: "hospital",
      component: () => import("../views/HospitalView/index.vue"),
      beforeEnter: [authGuard, repSignalGuard],
    },
    {
      path: "/organization/:org_id",
      name: "organization",
      component: () => import("../views/OrganizationView/index.vue"),
      beforeEnter: [authGuard, repSignalGuard],
    },
    {
      path: "/facility/:npi",
      name: "facility",
      component: () => import("../views/FacilityView/index.vue"),
      beforeEnter: [authGuard, repSignalGuard],
    },
    {
      path: "/health_system/:id",
      name: "health_system",
      component: () => import("../views/HealthSystemView/index.vue"),
      beforeEnter: [authGuard, repSignalGuard],
    },
    {
      path: "/lists",
      name: "lists",
      component: () => import("../views/ListView.vue"),
      beforeEnter: [authGuard, repSignalGuard],
      meta: {
        title: "Lists",
      },
    },
    {
      path: "/lists/:id",
      name: "listDetails",
      component: () => import("../views/ListDetailView/index.vue"),
      beforeEnter: [authGuard, repSignalGuard],
    },
    {
      path: "/account",
      name: "account",
      component: () => import("../views/AccountView/index.vue"),
      beforeEnter: authGuard,
      meta: {
        title: "My Account",
      },
    },
    {
      path: "/territory/optimizer",
      name: "territoryOptimizer",
      component: () => import("../views/TerritoryOptimizerView/index.vue"),
      beforeEnter: [authGuard, leaderGuard, repSignalGuard],
      meta: {
        title: "Territory Optimizer",
      },
    },
    {
      path: "/organization/builder",
      name: "organizationBuilder",
      component: () => import("../views/OrgBuilderView.vue"),
      beforeEnter: [authGuard, leaderGuard, repSignalGuard],
    },
    {
      path: "/open_payment",
      name: "openPayments",
      component: () => import("../views/OpenPaymentView/index.vue"),
      beforeEnter: [authGuard, repSignalGuard],
    },
    {
      path: "/newsfeed",
      name: "newsfeed",
      component: () => import("../views/NewsfeedView.vue"),
      beforeEnter: [authGuard, repSignalGuard],
      meta: {
        title: "News",
      },
    },
    {
      path: "/eula",
      name: "eula",
      component: () => import("../views/EulaView.vue"),
    },
    {
      path: "/privacy_policy",
      name: "privacy",
      alias: "/liveness_check",
      component: () => import("../views/PrivacyPolicyView.vue"),
    },
    {
      path: "/password_reset_verified/:token",
      name: "passwordReset",
      component: () => import("../views/PasswordResetView.vue"),
    },
    {
      path: "/setup-required",
      name: "setupRequired",
      component: () => import("../views/SetupRequiredView.vue"),
    },
    {
      path: "/universal_search/:search_type/:search_term",
      name: "universalSearch",
      component: () => import("../views/UniversalSearchView.vue"),
      beforeEnter: repSignalGuard,
      meta: {
        title: "Search",
      },
    },
    {
      path: "/marketsignal",
      name: "marketSignalHome",
      component: () => import("../views/MarketSignal/index.vue"),
      beforeEnter: [authGuard, marketSignalGuard],
      meta: {
        title: "Home",
        product: PRODUCTS.MARKETSIGNAL,
      },
    },
    {
      path: "/marketsignal/:key",
      name: "marketSignalDashboard",
      component: () => import("../views/MarketSignal/DashboardView.vue"),
      beforeEnter: [authGuard, marketSignalGuard],
      meta: {
        title: "Dashboard",
        product: PRODUCTS.MARKETSIGNAL,
      },
    },
    {
      path: "/:catchAll(.*)*",
      name: "NotFound",
      component: () => import("../views/PageNotFound.vue"),
    },
  ],
});

router.beforeEach((to) => {
  const isMarketSignal = to.meta?.product === PRODUCTS.MARKETSIGNAL;
  const productName = isMarketSignal ? "MarketSignal" : "RepSignal";
  document.title = to.meta?.title
    ? `${to.meta.title} - ${productName}`
    : productName;

  // The JotForm feedback button sits in the same bottom-right corner as the
  // Omni dashboards' "Blobby" assistant, so keep it out of MarketSignal.
  document.body.classList.toggle("hide-feedback-button", isMarketSignal);
});

// https://github.com/vitejs/vite/issues/11804#issuecomment-1406182566
router.onError((error, to) => {
  if (error.message.includes("Failed to fetch dynamically imported module")) {
    window.location = to.fullPath;
  }
});

export default router;
